'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { projectsAPI, type MinimalProject, type Project } from '../service/django-api';
import { useAuth } from './auth-context';

interface CartContextType {
  // Active project (cart)
  activeProject: MinimalProject | null
  activeProjectDetails: Project | null
  setActiveProject: (project: MinimalProject | null) => void
  
  // Projects list
  projects: MinimalProject[]
  loadProjects: () => Promise<void>
  refreshActiveProject: () => Promise<void>
  invalidateProjectCache: (projectId: string) => void
  
  // Cart operations
  addToCart: (projectId: string) => Promise<any>
  createProject: (name: string, description?: string) => Promise<MinimalProject>
  
  // Loading states
  isLoading: boolean
  isAddingToCart: boolean
  
  // Cart badge count
  cartCount: number
  
  // Extras and Gifts
  selectedExtras: Array<{ extraId: string; quantity: number; price: number }>
  selectedGifts: Array<{ giftId: string; quantity: number; value: number }>
  setSelectedExtras: (extras: Array<{ extraId: string; quantity: number; price: number }>) => void
  setSelectedGifts: (gifts: Array<{ giftId: string; quantity: number; value: number }>) => void
  clearExtrasAndGifts: () => void
  clearCart: () => Promise<void>
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const ACTIVE_PROJECT_KEY = 'mtg_active_project_id';
const SELECTED_EXTRAS_KEY = 'mtg_selected_extras';
const SELECTED_GIFTS_KEY = 'mtg_selected_gifts';

export function CartProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [activeProject, setActiveProjectState] = useState<MinimalProject | null>(null);
  const [activeProjectDetails, setActiveProjectDetails] = useState<Project | null>(null);
  const [projects, setProjects] = useState<MinimalProject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingToCart] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  
  const [projectDetailsCache, setProjectDetailsCache] = useState<Record<string, Project>>({});
  
  const [selectedExtras, setSelectedExtrasState] = useState<Array<{ extraId: string; quantity: number; price: number }>>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = localStorage.getItem(SELECTED_EXTRAS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (err) {
      console.error('[CartContext] Failed to load selectedExtras:', err);
      return [];
    }
  });
  
  const [selectedGifts, setSelectedGiftsState] = useState<Array<{ giftId: string; quantity: number; value: number }>>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = localStorage.getItem(SELECTED_GIFTS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (err) {
      console.error('[CartContext] Failed to load selectedGifts:', err);
      return [];
    }
  });
  
  const setSelectedExtras = (extras: Array<{ extraId: string; quantity: number; price: number }>) => {
    setSelectedExtrasState(extras);
    if (typeof window !== 'undefined') {
      localStorage.setItem(SELECTED_EXTRAS_KEY, JSON.stringify(extras));
    }
  };
  
  const setSelectedGifts = (gifts: Array<{ giftId: string; quantity: number; value: number }>) => {
    setSelectedGiftsState(gifts);
    if (typeof window !== 'undefined') {
      localStorage.setItem(SELECTED_GIFTS_KEY, JSON.stringify(gifts));
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadProjects();
    } else {
      setProjects([]);
      setActiveProjectState(null);
      setActiveProjectDetails(null);
      setCartCount(0);
      setSelectedExtras([]);
      setSelectedGifts([]);
      if (typeof window !== 'undefined') {
        localStorage.removeItem(ACTIVE_PROJECT_KEY);
        localStorage.removeItem(SELECTED_EXTRAS_KEY);
        localStorage.removeItem(SELECTED_GIFTS_KEY);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (activeProject) {
      refreshActiveProject();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.id]);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      const projectsList = await projectsAPI.getMinimalList();
      setProjects(projectsList);
      
      const savedProjectId = typeof window !== 'undefined' 
        ? localStorage.getItem(ACTIVE_PROJECT_KEY) 
        : null;
      
      if (savedProjectId) {
        const savedProject = projectsList.find(p => p.id === savedProjectId);
        if (savedProject) {
          setActiveProjectState(savedProject);
          return;
        }
      }
      
      if (!activeProject && projectsList.length > 0) {
        setActiveProjectState(projectsList[0]);
      }
    } catch (err) {
      console.error('[Cart] Failed to load projects:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshActiveProject = async () => {
    if (!activeProject) return;
    
    if (projectDetailsCache[activeProject.id]) {
      setActiveProjectDetails(projectDetailsCache[activeProject.id]);
      setCartCount(projectDetailsCache[activeProject.id].card_count);
      return;
    }
    
    try {
      const details = await projectsAPI.get(activeProject.id);
      setProjectDetailsCache(prev => ({
        ...prev,
        [activeProject.id]: details
      }));
      setActiveProjectDetails(details);
      setCartCount(details.card_count);
    } catch (err) {
      console.error('[Cart] Failed to load project details:', err);
    }
  };

  const setActiveProject = (project: MinimalProject | null) => {
    setActiveProjectState(project);
    if (typeof window !== 'undefined') {
      if (project) {
        localStorage.setItem(ACTIVE_PROJECT_KEY, project.id);
      } else {
        localStorage.removeItem(ACTIVE_PROJECT_KEY);
      }
    }
  };

  const createProject = async (name: string, description?: string): Promise<MinimalProject> => {
    try {
      setIsLoading(true);
      const newProject = await projectsAPI.create({
        name,
        description,
        cardstock: 'STANDARD',
      });
      
      const minimalProject: MinimalProject = {
        id: newProject.id,
        name: newProject.name,
        source: newProject.source,
        card_count: newProject.card_count,
        created_at: newProject.created_at,
        updated_at: newProject.updated_at,
      };
      
      setProjects(prev => [minimalProject, ...prev]);
      setActiveProject(minimalProject);
      
      if (typeof (window as any).notify === 'function') {
        (window as any).notify(`Cart "${name}" created!`, 3);
      }
      
      return minimalProject;
    } catch (err) {
      console.error('[Cart] Failed to create project:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // addToCart is stubbed out since this is the Yugioh Maker app.
  const addToCart = async (projectId: string) => {
    console.log('[Cart] addToCart stub called for project:', projectId);
    return Promise.resolve();
  };

  const clearExtrasAndGifts = () => {
    setSelectedExtras([]);
    setSelectedGifts([]);
  };

  const invalidateProjectCache = (projectId: string) => {
    setProjectDetailsCache(prev => {
      const newCache = { ...prev };
      delete newCache[projectId];
      return newCache;
    });
  };

  const clearCart = async () => {
    if (activeProject) {
      try {
        await projectsAPI.clearCards(activeProject.id);
        await refreshActiveProject();
      } catch (err) {
        console.error('[Cart] Failed to clear cart:', err);
      }
    }
    clearExtrasAndGifts();
  };

  return (
    <CartContext.Provider
      value={{
        activeProject,
        activeProjectDetails,
        setActiveProject,
        projects,
        loadProjects,
        refreshActiveProject,
        invalidateProjectCache,
        addToCart,
        createProject,
        isLoading,
        isAddingToCart,
        cartCount,
        selectedExtras,
        selectedGifts,
        setSelectedExtras,
        setSelectedGifts,
        clearExtrasAndGifts,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
